/**
 * Card de um passo no canvas do editor de automações (issue #137).
 *
 * Um único tipo de nó do xyflow (`automation`) desenha todos os passos; o que
 * muda é `data.nodeType`, resolvido no catálogo vindo do backend. Catálogo e
 * nomes (etapas/funis) chegam por contexto, não por `data`, para não vazarem
 * para o `definition` salvo.
 *
 * Conexões: entrada no topo (exceto gatilho); saída embaixo. Nós com vários
 * caminhos têm uma saída por caminho (ver nodeHandles): condição "Sim"/"Não",
 * esperar resposta "Respondeu"/"Não respondeu", switch um por valor + "Outros".
 */

import { createContext, memo, useContext, useEffect } from 'react'
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react'
import Icon from '../../components/icons.jsx'
import { metaFor, nodeHandles, summarize } from './nodeMeta.js'

export const EditorContext = createContext({ catalog: {}, names: {}, invalid: new Set(), testSteps: {} })

function AutomacaoNode({ id, data, selected }) {
  const { catalog, names, invalid, testSteps } = useContext(EditorContext)
  const test = testSteps?.[id]
  const type = catalog[data.nodeType]
  const meta = metaFor(type)
  const isTrigger = type?.kind === 'trigger'
  const branches = nodeHandles(type, data.config)
  // Switch: caminhos mudam com a config — o xyflow precisa remedir as saídas.
  const updateNodeInternals = useUpdateNodeInternals()
  const handleKey = branches?.map((h) => h.id).join('|') || ''
  useEffect(() => { updateNodeInternals(id) }, [id, handleKey, updateNodeInternals])

  return (
    <div
      className={[
        'aut-node', `tone-${meta.tone}`,
        selected ? 'is-selected' : '',
        invalid.has(id) ? 'is-invalid' : '',
        test ? `is-test-${test.status}` : '',
      ].filter(Boolean).join(' ')}
    >
      {!isTrigger && <Handle type="target" position={Position.Top} className="aut-handle" />}
      <div className="aut-node-head">
        <span className="aut-node-icon"><Icon n={meta.icon} s={15} /></span>
        <div className="aut-node-titles">
          <span className="aut-node-kind">{meta.kindLabel}</span>
          <span className="aut-node-label">{type?.label || data.nodeType}</span>
        </div>
        {test && (
          <span className={`aut-node-test is-${test.status}`}
            title={test.error || `Testado: ${test.status}`}>
            <Icon n={test.status === 'error' ? 'alert' : 'check'} s={13} />
          </span>
        )}
        {invalid.has(id) && (
          <span className="aut-node-warn" title="Configuração incompleta">
            <Icon n="alert" s={14} />
          </span>
        )}
      </div>
      <p className="aut-node-summary">{summarize(data.nodeType, data.config, type, names)}</p>

      {branches ? (
        <div className="aut-node-branches">
          {branches.map((h, i) => (
            <div key={h.id} className={`aut-branch aut-branch-${h.id}`}>
              <span title={h.label}>{h.label}</span>
              <Handle
                type="source" id={h.id} position={Position.Bottom}
                className={`aut-handle aut-handle-${h.id}`}
                style={{ left: `${((i + 1) / (branches.length + 1)) * 100}%` }}
              />
            </div>
          ))}
        </div>
      ) : (
        <Handle type="source" position={Position.Bottom} className="aut-handle" />
      )}
    </div>
  )
}

export default memo(AutomacaoNode)
